import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDays, ChevronDown, ChevronUp, Filter, Globe, RefreshCw, Search,
  ShieldCheck, SlidersHorizontal, X,
} from 'lucide-react';
import client from '../lib/api';
import { AdminEmpty, AdminError, AdminSkeleton } from './components/PageState';
import { formatDateTime } from './lib/formatters';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import {
  ADVANCED_KEYS, DATE_PRESETS, EMPTY_FILTERS, actorInitial, activePreset,
  filterChips, formatTime, groupByDay, presetRange, severityBadge,
  severityTone, targetLabel,
} from './lib/auditLog';


const EMPTY_FACETS = {
  sections: [], actors: [], roles: [], actions: [], target_types: [],
};

const AuditLog = () => {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [total, setTotal] = useState(0);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);

  // Süzgeçler ve sayfa **adres çubuğundadır**, bileşen state'inde değil:
  // yönetici bir kaydı açıp üyenin profiline geçtiğinde ve geri döndüğünde
  // arama sessizce sıfırlanıyordu — aradığı satırı baştan bulması
  // gerekiyordu. Aynı sebeple bağlantı paylaşılabilir hâle gelir (kupon
  // ekranındaki `?override=` kararının aynısı).
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => {
    const next = { ...EMPTY_FILTERS };
    Object.keys(EMPTY_FILTERS).forEach((key) => {
      next[key] = params.get(key) || '';
    });
    return next;
  }, [params]);

  const skip = Math.max(0, Number(params.get('skip')) || 0);

  const writeParams = useCallback((nextFilters, nextSkip) => {
    const next = new URLSearchParams();
    Object.entries(nextFilters).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });
    if (nextSkip > 0) next.set('skip', String(nextSkip));
    // `replace`: her tuş değişikliği geçmişe bir giriş eklerse yöneticinin
    // geri düğmesi süzgeçler arasında gezinir, sayfadan çıkmaz.
    setParams(next, { replace: true });
  }, [setParams]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(() => readPageSize('audit', 50));
  const [error, setError] = useState('');

  const requestParams = useMemo(() => {
    const params = { skip, limit: pageSize };
    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;
      if (key === 'date_from') {
        params.date_from = `${value}T00:00:00`;
      } else if (key === 'date_to') {
        params.date_to = `${value}T23:59:59`;
      } else {
        params[key] = value;
      }
    });
    return params;
  }, [filters, pageSize, skip]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/audit-logs', {
        params: requestParams,
      });
      setItems(data.items || []);
      setFacets(data.facets || EMPTY_FACETS);
      setTotal(data.total || 0);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
        || 'Yönetim işlem geçmişi alınamadı',
      );
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => { load(); }, [load]);

  // Adres değişince form da onu göstermelidir; yazarken tetiklenmez çünkü
  // taslak adres çubuğuna yazılmaz (yalnızca Uygula yazar).
  useEffect(() => { setDraftFilters(filters); }, [filters]);

  const chips = filterChips(filters, facets);
  const groups = groupByDay(items);
  const preset = activePreset(filters);
  const advancedCount = ADVANCED_KEYS.filter((key) => filters[key]).length;

  // Filtreler iki yolla uygulanır: form gönderimi taslağı yürürlüğe koyar,
  // kısayollar (tarih çipi, çip kaldırma) anında uygulanır. İkisi de sayfayı
  // başa sarar, yoksa daralan sonuçta boş bir sayfada kalınır.
  const commit = (next) => {
    setDraftFilters(next);
    writeParams(next, 0);
    setExpanded('');
  };

  const applyFilters = (event) => {
    event.preventDefault();
    commit({ ...draftFilters });
  };

  const clearFilters = () => commit({ ...EMPTY_FILTERS });

  const removeChip = (key) => commit({ ...filters, [key]: '' });

  const choosePreset = (id) => commit({ ...filters, ...presetRange(id) });

  const updateFilter = (key, value) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="admin-audit">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-violet-400/10 text-violet-300 border border-violet-400/20">
          <ShieldCheck size={19} aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-xl font-black">Yönetim İşlem Geçmişi</h1>
          <p className="text-xs text-slate-400">
            Yetkililerin yaptığı değişiklikleri anlaşılır ayrıntılarıyla inceleyin
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="icon-btn view ml-auto"
          aria-label="İşlem geçmişini yenile"
          title="Yenile"
        >
          <RefreshCw size={15} aria-hidden="true" />
        </button>
      </div>

      <form onSubmit={applyFilters} className="admin-card admin-audit-filters">
        <div className="admin-audit-filter-bar">
          <div className="admin-audit-search">
            <label htmlFor="audit-search" className="sr-only">
              Yönetici, kullanıcı veya işlem ara
            </label>
            <Search size={15} aria-hidden="true" />
            <input
              id="audit-search"
              value={draftFilters.q}
              onChange={(event) => updateFilter('q', event.target.value)}
              className="admin-input pl-10"
              placeholder="Yönetici, kullanıcı veya işlem ara"
            />
          </div>

          <div
            className="admin-audit-presets"
            role="group"
            aria-label="Hızlı tarih aralığı"
          >
            {DATE_PRESETS.map((item) => (
              <button
                type="button"
                key={item.id}
                onClick={() => choosePreset(item.id)}
                className={`admin-tab ${preset === item.id ? 'active' : ''}`}
                aria-pressed={preset === item.id}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setAdvancedOpen((open) => !open)}
            className="admin-btn-secondary admin-audit-advanced-toggle"
            aria-expanded={advancedOpen}
            aria-controls="audit-advanced"
          >
            <SlidersHorizontal size={14} aria-hidden="true" />
            Gelişmiş
            {advancedCount > 0 && (
              <span className="admin-audit-advanced-count">{advancedCount}</span>
            )}
            {advancedOpen
              ? <ChevronUp size={14} aria-hidden="true" />
              : <ChevronDown size={14} aria-hidden="true" />}
          </button>

          <button type="submit" className="admin-btn-primary">
            <Search size={14} aria-hidden="true" /> Ara
          </button>
        </div>

        {advancedOpen && (
          <div id="audit-advanced" className="admin-audit-advanced">
            <label>
              <span>İşlemi yapan yetkili</span>
              <select
                value={draftFilters.admin_id}
                onChange={(event) => updateFilter('admin_id', event.target.value)}
                className="admin-input"
              >
                <option value="">Tüm yetkililer</option>
                {(facets.actors || []).map((actor) => (
                  <option key={actor.id} value={actor.id}>
                    {actor.username} — {actor.role_label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Yetkili rolü</span>
              <select
                value={draftFilters.role}
                onChange={(event) => updateFilter('role', event.target.value)}
                className="admin-input"
              >
                <option value="">Tüm roller</option>
                {(facets.roles || []).map((role) => (
                  <option key={role.id} value={role.id}>{role.label}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Bölüm</span>
              <select
                value={draftFilters.section}
                onChange={(event) => updateFilter('section', event.target.value)}
                className="admin-input"
              >
                <option value="">Tüm bölümler</option>
                {(facets.sections || []).map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>İşlem</span>
              <select
                value={draftFilters.action}
                onChange={(event) => updateFilter('action', event.target.value)}
                className="admin-input"
              >
                <option value="">Tüm işlemler</option>
                {(facets.actions || []).map((action) => (
                  <option key={action.id} value={action.id}>{action.label}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Etkilenen kayıt</span>
              <select
                value={draftFilters.target_type}
                onChange={(event) => updateFilter('target_type', event.target.value)}
                className="admin-input"
              >
                <option value="">Tüm kayıt türleri</option>
                {(facets.target_types || []).map((target) => (
                  <option key={target} value={target}>{targetLabel(target)}</option>
                ))}
              </select>
            </label>

            <label>
              <span>Başlangıç tarihi</span>
              <div className="admin-audit-date">
                <CalendarDays size={15} aria-hidden="true" />
                <input
                  type="date"
                  value={draftFilters.date_from}
                  onChange={(event) => updateFilter('date_from', event.target.value)}
                  className="admin-input pl-10"
                />
              </div>
            </label>

            <label>
              <span>Bitiş tarihi</span>
              <div className="admin-audit-date">
                <CalendarDays size={15} aria-hidden="true" />
                <input
                  type="date"
                  value={draftFilters.date_to}
                  onChange={(event) => updateFilter('date_to', event.target.value)}
                  className="admin-input pl-10"
                />
              </div>
            </label>
          </div>
        )}

        {chips.length > 0 && (
          <div className="admin-audit-chips">
            <Filter size={13} aria-hidden="true" />
            {chips.map((chip) => (
              <button
                type="button"
                key={chip.key}
                className="admin-audit-chip"
                onClick={() => removeChip(chip.key)}
                aria-label={`${chip.label} filtresini kaldır`}
              >
                <span>{chip.label}:</span>
                <strong>{chip.value}</strong>
                <X size={12} aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearFilters}
              className="admin-audit-chip-clear"
            >
              Tümünü temizle
            </button>
          </div>
        )}
      </form>

      {loading ? (
        <AdminSkeleton rows={8} label="İşlem geçmişi yükleniyor" />
      ) : error ? (
        <AdminError message={error} onRetry={load} />
      ) : items.length === 0 ? (
        <div className="admin-card">
          <AdminEmpty
            title="Bu filtrelere uygun işlem bulunamadı"
            description="Filtreleri temizleyerek daha geniş bir tarih aralığı deneyin."
          />
        </div>
      ) : (
        <>
          <div className="admin-audit-count" aria-live="polite">
            <strong>{total}</strong> işlem bulundu
            {chips.length > 0 && ' · filtrelenmiş sonuç'}
          </div>

          {groups.map((group) => (
            <section key={group.key} className="admin-audit-day">
              <header className="admin-audit-day-head">
                {group.relative && (
                  <span className="admin-audit-day-relative">
                    {group.relative}
                  </span>
                )}
                <h2>{group.title}</h2>
                <span className="admin-audit-day-count">
                  {group.items.length} işlem
                </span>
              </header>

              <ol className="admin-audit-list">
                {group.items.map((item) => {
                  const isExpanded = expanded === item.id;
                  const details = item.detail_items || [];
                  return (
                    <li
                      key={item.id}
                      className={`admin-audit-entry ${severityTone(item.severity)} ${isExpanded ? 'is-open' : ''}`}
                    >
                      <span className="admin-audit-time">
                        {formatTime(item.created_at, '--:--')}
                      </span>
                      <span className="admin-audit-rail" aria-hidden="true" />
                      <div className="admin-audit-card">
                        <button
                          type="button"
                          onClick={() => setExpanded(isExpanded ? '' : item.id)}
                          className="admin-audit-summary"
                          aria-expanded={isExpanded}
                        >
                          <span className="admin-audit-avatar" aria-hidden="true">
                            {actorInitial(item.admin_username)}
                          </span>
                          <span className="admin-audit-body">
                            <span className="admin-audit-line">
                              <strong>
                                {item.admin_username || 'Bilinmeyen yetkili'}
                              </strong>
                              <span className="badge-pill badge-purple">
                                {item.admin_role_label || 'Yetkili'}
                              </span>
                              <span className={`badge-pill ${severityBadge(item.severity)}`}>
                                {item.action_label || 'Yönetim işlemi'}
                              </span>
                            </span>
                            <span className="admin-audit-text">
                              {item.summary || item.action_label}
                            </span>
                            <span className="admin-audit-meta">
                              <span>{item.section_label || 'Yönetim'}</span>
                              {item.target_label && (
                                <>
                                  <span aria-hidden="true">•</span>
                                  <span>Etkilenen: {item.target_label}</span>
                                </>
                              )}
                              {details.length > 0 && (
                                <>
                                  <span aria-hidden="true">•</span>
                                  <span>{details.length} ayrıntı</span>
                                </>
                              )}
                            </span>
                          </span>
                          <span className="admin-audit-chevron" aria-hidden="true">
                            {isExpanded
                              ? <ChevronUp size={16} />
                              : <ChevronDown size={16} />}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="admin-audit-detail">
                            {details.length > 0 ? (
                              <dl className="admin-audit-detail-grid">
                                {details.map((detail) => (
                                  <div key={`${item.id}-${detail.key}`}>
                                    <dt>{detail.label}</dt>
                                    <dd>{detail.value}</dd>
                                  </div>
                                ))}
                              </dl>
                            ) : (
                              <p className="admin-audit-detail-empty">
                                Bu işlem için ek ayrıntı bulunmuyor.
                              </p>
                            )}
                            <div className="admin-audit-detail-foot">
                              <span>
                                <CalendarDays size={12} aria-hidden="true" />
                                {formatDateTime(item.created_at)}
                              </span>
                              {item.ip_address && (
                                <span>
                                  <Globe size={12} aria-hidden="true" />
                                  {item.ip_address}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}

          <div className="admin-card px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              disabled={skip === 0}
              onClick={() => {
                writeParams(filters, Math.max(0, skip - pageSize));
                setExpanded('');
              }}
              className="admin-btn-secondary"
            >
              Önceki
            </button>
            <span className="text-xs text-slate-400">
              {total ? skip + 1 : 0}–{Math.min(skip + pageSize, total)} / {total}
            </span>
            <button
              type="button"
              disabled={skip + pageSize >= total}
              onClick={() => {
                writeParams(filters, skip + pageSize);
                setExpanded('');
              }}
              className="admin-btn-secondary ml-auto"
            >
              Sonraki
            </button>
            <PageSizeSelect
              value={pageSize}
              disabled={loading}
              onChange={(size) => {
                // Sayfa adres çubuğunda durduğu için başa dönüş de oradan
                // yazılır; state'te bırakmak listeyi süzgeçle ayrıştırırdı.
                setPageSize(size);
                writePageSize('audit', size);
                writeParams(filters, 0);
                setExpanded('');
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AuditLog;
